import productsServiceCommon from '../';
import constants from '../../../settings/constants';
import { articleNo } from '../../../util/articleNo';
import { getProppingBounds, isStandAlone } from '../models';
import { config as asConfig } from '../../../scene/boaxel/AdjustableConfig';
import { generateProppingImageResources } from '../index';
import { FILTERS, ITEMS, MISSING_PRODUCT_CATEGORIES } from '../../../constants';
import { colorFilterObject, subFilterObject } from '../itemsFilters';

const BRACKET_IDS = {
  white: '60448733',
  anthracite: '10575587',
};

const articleImages = [];
const fitCache = {};

/**
 * See {@link getFilters} for docs on filters structure.
 * @type {[{filter: (function(*=): function({product: *}): boolean), condition: (function(): boolean), Component: () => JSX.Element}]}
 */
const getFilters = () => [
  colorFilterObject(),
  subFilterObject({ appliesTo: [FILTERS.UPRIGHT] }),
];

function isImageDummy(item) {
  return item.type === 'dummy';
}

function makeArticleImages() {
  const imageList = productsServiceCommon
    .getAll()
    .filter(
      item =>
        item.modelid &&
        (productsServiceCommon.isRealArticleProduct(item) || isImageDummy(item))
    )
    .map(product => {
      const { color } = product.filter;
      let articleId = product.id;

      if (!articleNo(articleId)) {
        const includedArticles = productsServiceCommon.getIncludedArticles(
          product.id
        );

        while (includedArticles.length && !articleNo(articleId)) {
          articleId = includedArticles.shift();
        }
      }

      const sceneFileName = `${product.modelid}_${color}_0001.png`;
      const menuFileName = `${product.modelid}_${color}_t_0001.png`;
      const hasPortraitSpecificThumb =
        productsServiceCommon.isType(product, ITEMS.UPRIGHT) ||
        productsServiceCommon.isExtendable(product);

      const hasMobileLandscapeSpecificThumb = productsServiceCommon.isType(
        product,
        ITEMS.UPRIGHT
      );

      const hasMobilePortraitSpecificThumb = productsServiceCommon.isType(
        product,
        [
          ITEMS.CLOTHES_RAIL,
          ITEMS.DRYING_RACK,
          ITEMS.TROUSER_HANGER,
          ITEMS.SHOE_SHELF,
        ]
      );

      return {
        name: product.id,
        color: color,
        type: product.filter.type,
        modelid: product.modelid,
        proppingBounds: getProppingBounds(product),
        url: `${constants.IMAGE_ROOT}${sceneFileName}`,
        thumbnailUrl: `${constants.IMAGE_ROOT}thumbnails/${menuFileName}`,
        thumbnailUrlPortrait: hasPortraitSpecificThumb
          ? `${constants.IMAGE_ROOT}thumbnails/portrait/${menuFileName}`
          : null,
        thumbnailUrlMobilePortrait: hasMobilePortraitSpecificThumb
          ? `${constants.IMAGE_ROOT}thumbnails/mobile-portrait/${menuFileName}`
          : null,
        thumbnailUrlMobileLandscape: hasMobileLandscapeSpecificThumb
          ? `${constants.IMAGE_ROOT}thumbnails/mobile-landscape/${menuFileName}`
          : null,
      };
    });

  articleImages.push(...generateProppingImageResources(imageList));
}

function getArticleImage(id) {
  return articleImages.find(image => image.name === id);
}

function getDragMode(product) {
  //Not yet implemented for Boaxel
  return constants.DRAG_MODE.FLOAT;
}

function isStackable(item) {
  //Not yet implemented for Boaxel
  return false;
}

function getWidth(item, slot) {
  const range = {
    max: asConfig.section.maxWidth,
    min: asConfig.section.minWidth,
  };
  if (
    (slot.logic && slot.logic.extendable) ||
    (item.logic && item.logic.extendable)
  ) {
    if (slot.width <= range.max && slot.width >= range.min) {
      return range;
    }
  }
  return slot.width;
}

function isClothesRail(id) {
  return productsServiceCommon.isType(id, ITEMS.CLOTHES_RAIL);
}

function isSection(id) {
  const found = productsServiceCommon.getProduct(id, true);
  return found && found.filter.type === ITEMS.SECTION;
}

function getFit(item, slot) {
  let fits;
  switch (item.filter.type) {
    case ITEMS.TABLE:
      fits = productsServiceCommon.getFilteredItems(
        productsServiceCommon.isTable,
        {
          width: getWidth(item, slot),
          color: slot.color || item.filter.color,
        }
      );
      break;
    case ITEMS.SHELF:
      fits = productsServiceCommon.getFilteredItems(
        productsServiceCommon.isShelf,
        {
          width: getWidth(item, slot),
          color: slot.color || item.filter.color,
        }
      );
      break;
    case ITEMS.METAL_SHELF:
      fits = productsServiceCommon.getFilteredItems(
        item => productsServiceCommon.isType(item, ITEMS.METAL_SHELF),
        {
          width: getWidth(item, slot),
          color: slot.color || item.filter.color,
        }
      );
      break;
    case ITEMS.WIRE_SHELF:
      fits = productsServiceCommon.getFilteredItems(
        item => productsServiceCommon.isType(item, ITEMS.WIRE_SHELF),
        {
          width: getWidth(item, slot),
          color: slot.color || item.filter.color,
        }
      );
      break;
    case ITEMS.SHOE_SHELF:
      fits = productsServiceCommon.getFilteredItems(
        item => productsServiceCommon.isType(item, ITEMS.SHOE_SHELF),
        {
          width: getWidth(item, slot),
          color: slot.color || item.filter.color,
        }
      );
      break;
    case ITEMS.BASKET:
      fits = productsServiceCommon.getFilteredItems(
        item => productsServiceCommon.isType(item, ITEMS.BASKET),
        {
          width: slot.width,
          color: slot.color || item.filter.color,
        }
      );
      break;
    case ITEMS.DRYING_RACK:
      fits = productsServiceCommon.getFilteredItems(
        item => productsServiceCommon.isType(item, ITEMS.DRYING_RACK),
        {
          width: slot.width,
          color: slot.color || item.filter.color,
        }
      );
      break;
    case ITEMS.SECTION:
      fits = productsServiceCommon.getFilteredItems(isSection, {
        width: slot.width,
      });
      if (
        !fits.length &&
        slot.width <= asConfig.section.maxWidth &&
        slot.width >= asConfig.section.minWidth
      ) {
        fits = productsServiceCommon.getFilteredItems(isSection, {
          width: asConfig.section.minWidth,
        });
        if (fits[0]) {
          fits[0] = { ...fits[0], width: slot.width };
        }
      }
      break;
    case ITEMS.CLOTHES_RAIL:
      if (fitCache[item.id]) {
        const oldFit = fitCache[item.id];
        if (
          oldFit.width === slot.width &&
          oldFit.filter?.color === slot.filter?.color
        ) {
          return item;
        }
      }
      fits = productsServiceCommon.getFilteredItems(isClothesRail, {
        width: getWidth(item, slot),
        color: slot.color || item.filter.color,
      });
      break;
    default:
      fits = productsServiceCommon.getFilteredItems(
        other =>
          productsServiceCommon.isType(
            other,
            slot.filter ? slot.filter.type : item.filter.type
          ),
        {
          width: slot.width,
        }
      );
      break;
  }

  if (fits && fits.length) {
    const fit = Object.assign({}, fits[0]);
    if (getWidth(slot, item) !== slot.width) {
      fit.width = slot.width;
    } else {
      // this was a static item, cache it
      fitCache[fit.id] = slot;
    }
    return fit;
  }
  return null;
}

function isModule(id) {
  //Not yet implemented for Boaxel
  return false;
}

function isInsert(id) {
  return !productsServiceCommon.isType(id, [
    ITEMS.SECTION,
    ITEMS.UPRIGHT,
    ITEMS.BRACKET,
    ITEMS.CLOTHES_RAIL,
    ITEMS.MOUNTING_RAIL,
  ]);
}

function getFittingBracket(item, side, color) {
  if (isStandAlone(item)) {
    return null;
  }

  if (
    productsServiceCommon.isType(item, [
      ITEMS.SECTION,
      ITEMS.MOUNTING_RAIL,
      ITEMS.CLOTHES_RAIL,
      ITEMS.BRACKET,
    ])
  ) {
    return null;
  }

  if (color && !BRACKET_IDS[color]) {
    return null;
  }

  const bracketIdsToConsider = color
    ? [BRACKET_IDS[color]]
    : Object.values(BRACKET_IDS);
  const bracketProduct = bracketIdsToConsider
    .map(bracketId => productsServiceCommon.getProduct(bracketId))
    .find(product => product !== undefined);

  return bracketProduct;
}

function isDependentItem(item) {
  return productsServiceCommon.isType(item, [
    ITEMS.SECTION,
    ITEMS.BRACKET,
    ITEMS.MOUNTING_RAIL,
  ]);
}

function isAdjustable(item) {
  return isSection(item) && item.logic && item.logic.extendable;
}

function shouldHaveColorSelector(item) {
  return productsServiceCommon.isType(item, [
    ITEMS.UPRIGHT,
    ITEMS.SHELF,
    ITEMS.METAL_SHELF,
    ITEMS.WIRE_SHELF,
    ITEMS.SHOE_SHELF,
    ITEMS.BASKET,
    ITEMS.DRYING_RACK,
    ITEMS.CLOTHES_RAIL,
    ITEMS.TABLE,
  ]);
}

function shouldHaveHeadlineForSelectors(item) {
  return productsServiceCommon.isType(item, [
    ITEMS.SHELF,
    ITEMS.METAL_SHELF,
    ITEMS.WIRE_SHELF,
  ]);
}

function fitsTable(section) {
  return productsServiceCommon
    .getFilteredItems(item => productsServiceCommon.isType(item, ITEMS.TABLE))
    .some(table => table.width === section.width);
}

function getSectionInserts(...filters) {
  return productsServiceCommon.getFilteredItems(
    item =>
      productsServiceCommon.isType(item, [
        ITEMS.SHELF,
        ITEMS.METAL_SHELF,
        ITEMS.WIRE_SHELF,
        ITEMS.TABLE,
      ]),
    ...filters
  );
}

function canHaveOverlappingDropAreas(item) {
  return productsServiceCommon.isTable(item);
}

function areMountingRailsOfSpecificColorValid(color) {
  const mountingRails = productsServiceCommon.getFilteredItems(item =>
    productsServiceCommon.isType(item, ITEMS.MOUNTING_RAIL)
  );

  const shortRail = mountingRails.find(
    rail => rail.id === constants.MOUNTING_RAILS[color][600]
  );
  const longRail = mountingRails.find(
    rail => rail.id === constants.MOUNTING_RAILS[color][800]
  );

  const valid = !!(shortRail?.valid && longRail?.valid);
  return valid;
}

function getValidMountingRailColors() {
  const candidateColors = Object.keys(constants.MOUNTING_RAILS);
  const colorsWithValidMountingRails = candidateColors.filter(candidateColor =>
    areMountingRailsOfSpecificColorValid(candidateColor)
  );

  return colorsWithValidMountingRails;
}

function areMountingRailsValid() {
  return !!getValidMountingRailColors().length;
}

function getMissingMandatoryProductCategories() {
  const uprights = productsServiceCommon.getFilteredItems(item =>
    productsServiceCommon.isType(item, ITEMS.UPRIGHT)
  );
  const brackets = productsServiceCommon.getFilteredItems(item =>
    productsServiceCommon.isType(item, ITEMS.BRACKET)
  );

  const missingProductCategories = [];
  if (uprights.length === 0)
    missingProductCategories.push(MISSING_PRODUCT_CATEGORIES.UPRIGHT);
  if (brackets.length === 0)
    missingProductCategories.push(MISSING_PRODUCT_CATEGORIES.BRACKET);

  return missingProductCategories;
}

export default {
  articleImages,
  canHaveOverlappingDropAreas,
  getArticleImage,
  getFittingBracket,
  getDragMode,
  getFit,
  isAdjustable,
  isModule,
  isInsert,
  isSection,
  isStackable,
  isDependentItem,
  makeArticleImages,
  shouldHaveColorSelector,
  shouldHaveHeadlineForSelectors,
  fitsTable,
  getSectionInserts,
  areMountingRailsOfSpecificColorValid,
  getValidMountingRailColors,
  areMountingRailsValid,
  getMissingMandatoryProductCategories,
  getFilters,
};
